import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { useAppDispatch } from "../../../redux/hooks";
import { resolveScopedVaultApi } from "../../../api/vault.api";
import { vaultKeys } from "../queryKeys";
import { setActiveRequirement } from "../../../redux/slices/vaultSlice";
import { setActiveRequirement as setAdminActiveRequirement } from "../../../redux/slices/requirementsSlice";
import { getExceptionMessage } from "../../../exceptions/helpers/getExceptionMessage";
import { vaultExceptions } from "../../../exceptions/default/vaultExceptions";
import { VaultFormValidationError } from "../../../exceptions/VaultFormValidationError";
import type {
  Folder,
  FormValue,
  Requirement,
} from "../../../redux/interfaces/folder.types";

interface UpdateFormValuesParams {
  reqId: string;
  form_values: FormValue[] | null;
  status?: string;
  upload_batch_id?: string;
  upload_batch_action?: "replace_evidence_on_submit";
}

type FolderWithSubfolders = Folder & {
  subfolders: NonNullable<Folder["subfolders"]>;
};

function isRecord(data: unknown): data is Record<string, unknown> {
  return typeof data === "object" && data !== null;
}

function isStringRecord(data: unknown): data is Record<string, string> {
  return (
    isRecord(data) &&
    Object.values(data).every((value) => typeof value === "string")
  );
}

function getVaultErrorData(error: unknown): Record<string, unknown> | null {
  if (!isRecord(error)) return null;

  const response = error.response;
  if (!isRecord(response)) return null;

  const data = response.data;
  return isRecord(data) ? data : null;
}

function replaceRequirementInFolder(
  folder: FolderWithSubfolders,
  updatedRequirement: Requirement,
): Folder | null {
  let folderChanged = false;
  const subfolders = folder.subfolders.map((subfolder) => {
    let subfolderChanged = false;
    const requirements = subfolder.requirements.map((requirement) => {
      if (requirement.id !== updatedRequirement.id) return requirement;

      subfolderChanged = true;
      return updatedRequirement;
    });

    if (!subfolderChanged) return subfolder;

    folderChanged = true;
    return { ...subfolder, requirements };
  });

  return folderChanged ? { ...folder, subfolders } : null;
}

function isTargetFolderData(
  data: unknown,
  folderId: string,
): data is FolderWithSubfolders {
  return (
    isRecord(data) && data.id === folderId && Array.isArray(data.subfolders)
  );
}

function patchCachedFolderRequirement(
  queryClient: QueryClient,
  folderId: string,
  updatedRequirement: Requirement,
) {
  const cachedFolderQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: vaultKeys.folder(folderId) });

  cachedFolderQueries.forEach((query) => {
    const folder = query.state.data;
    if (!isTargetFolderData(folder, folderId)) return;

    const updatedFolder = replaceRequirementInFolder(
      folder,
      updatedRequirement,
    );
    if (!updatedFolder) return;

    queryClient.setQueryData(query.queryKey, updatedFolder);
  });
}

export function useUpdateVaultFormValues(folderId: string | undefined) {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const scopedVaultApi = resolveScopedVaultApi(
    typeof window !== "undefined" ? window.location.pathname : "",
  );

  return useMutation({
    mutationFn: async ({
      reqId,
      form_values,
      status,
      upload_batch_id,
      upload_batch_action,
    }: UpdateFormValuesParams) => {
      try {
        // Omit `status` entirely when undefined so the backend keeps the
        // current status (used by the admin in-place edit of valid
        // requirements). Sending it only when provided also avoids relying
        // on the serializer dropping undefined keys.
        return await scopedVaultApi.updateFormValues(reqId, {
          form_values,
          ...(status !== undefined ? { status } : {}),
          ...(upload_batch_id !== undefined ? { upload_batch_id } : {}),
          ...(upload_batch_action !== undefined ? { upload_batch_action } : {}),
        });
      } catch (error: unknown) {
        const data = getVaultErrorData(error);
        const code = typeof data?.code === "string" ? data.code : undefined;
        const fields = isStringRecord(data?.fields) ? data.fields : null;
        // Form conflicts and Buró company format errors retain the
        // field → message map so the form can mark the exact fields,
        // identically to client-side validation.
        if ((code === "RV001" || code === "BDC060") && fields) {
          throw new VaultFormValidationError(
            code === "BDC060" && typeof data?.detail === "string"
              ? data.detail
              : getExceptionMessage(code, vaultExceptions.updateVaultFormValues),
            fields,
          );
        }
        // RT001: backend rejected because the requirement is already valid.
        // The local React state is stale (staff approved between page load
        // and submit). Refetch the folder so the UI re-renders with the
        // up-to-date status and the form locks itself.
        if (code === "RT001" && folderId) {
          await queryClient.invalidateQueries({
            queryKey: vaultKeys.folder(folderId),
          });
        }
        const backendMessage = data?.error ?? data?.detail;
        if (
          code?.startsWith("UPLOAD_") &&
          typeof backendMessage === "string" &&
          backendMessage.length > 0
        ) {
          throw new Error(backendMessage);
        }
        throw new Error(
          getExceptionMessage(code, vaultExceptions.updateVaultFormValues),
        );
      }
    },
    onSuccess: async (data) => {
      if (folderId) {
        patchCachedFolderRequirement(queryClient, folderId, data);
        await queryClient.invalidateQueries({
          queryKey: vaultKeys.folder(folderId),
        });
      }
      dispatch(setActiveRequirement(data));
      dispatch(setAdminActiveRequirement(data));
    },
  });
}
